// Typed fetch wrapper for the staff API.
//
// Mirrors api.ts's core deliberately rather than sharing its module-scope
// tokens: a vendor and a staff member are never the same session, and one
// token cell cannot hold both. Tokens live in module scope; only the refresh
// token is also kept in sessionStorage for the tab (see below).

import { ApiError, AuthError } from "@/lib/api";
import type { Employee, EmployeeStatus, Order, OrderLine, OrderType } from "@/lib/api";

// --- Types ---

interface ApiResponse<T> {
  data: T | null;
  error: { code: string; message: string } | null;
  meta: Record<string, unknown>;
}

export type StaffRole = "it" | "safe_environment" | "accounting";

export interface StaffUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: StaffRole;
}

/** The dashboard's views, one per tab (server: STAFF_VIEWS). */
export type StaffView = "today" | "verify" | "process" | "badges" | "all";

export interface StageCorrection {
  by: string;
  at: string;
  from: EmployeeStatus;
  to: EmployeeStatus;
  reason: string;
  previousClearance?: {
    clearanceDate: string;
    clearanceAffirmedBy?: string;
    clearanceAffirmedAt?: string;
  };
}

/** Mirror of the server's StageHistoryAction (src/api/src/lib/types.ts). */
export type StageHistoryAction =
  | "confirm_clearance"
  | "not_found"
  | "advance"
  | "entered"
  | "badge_made"
  | "delivered"
  | "photo_on_file"
  | "offboard"
  | "reactivate";

export interface StageHistoryEntry {
  at: string;
  /** A staff id, or "vendor:<vendorId>". */
  by: string;
  action: StageHistoryAction;
  from: EmployeeStatus;
  to: EmployeeStatus;
}

/** An employee as staff see it: the vendor projection plus the staff-only fields. */
export interface StaffEmployee extends Employee {
  clearanceAffirmedBy?: string;
  clearanceAffirmedAt?: string;
  stageCorrections?: StageCorrection[];
  stageHistory?: StageHistoryEntry[];
}

/** An employee read with its etag, which every staff write must send back. */
export interface StaffEmployeeWithEtag extends StaffEmployee {
  etag: string;
}

export interface VendorContact {
  companyName: string;
  contactName: string;
  contactPhone: string;
  email: string;
}

export interface StaffEmployeeRow extends StaffEmployeeWithEtag {
  /** Null when the vendor document cannot be found; the row still shows. */
  vendor: VendorContact | null;
  /** True only when IT is viewing a hidden company's employees. */
  vendorHidden: boolean;
  waitingOnYou: boolean;
  /** When they reached their current stage, as far as the record shows. */
  stageSince: string;
}

/** Any employee a dashboard row shows: queue rows carry the vendor contact and
 *  stageSince, a replacement row's employee carries neither. */
export type QueueEmployee = StaffEmployeeWithEtag & { vendor?: VendorContact | null; stageSince?: string };

/** One replacement badge still to make. */
export interface ReplacementRow {
  order: { id: string; vendorId: string; createdAt: string };
  employeeId: string;
  /** Null when the employee document is missing; the row still shows. */
  employee: StaffEmployeeWithEtag | null;
  offboarded: boolean;
  clearanceDate: string | null;
}

export interface StaffQueueCounts {
  verify: number;
  process: number;
  badges: number;
  all: number;
}

export interface StaffQueueMeta {
  counts: StaffQueueCounts;
  /** Today only. */
  recentActivity?: StaffEmployeeRow[];
}

export type { SortDir } from "@/lib/list-url";

/** One list on a dashboard tab (server: SECTION_KEYS). */
export type SectionKey =
  | "verify"
  | "move_forward"
  | "enter"
  | "make"
  | "delivered"
  | "waiting_on_you"
  | "waiting_on_others"
  | "all";

/** A queue section's Sort by menu (server: QUEUE_SORTS). */
export type QueueSort = "waiting" | "company" | "name" | "stage";

/** One page of a server-sorted list. */
export interface Page<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** A replacement badge to make, with its company for the link. */
export type StaffReplacementRow = ReplacementRow & { vendor: VendorContact | null };

export type SectionRow =
  | { kind: "employee"; row: StaffEmployeeRow }
  | { kind: "replacement"; row: StaffReplacementRow };

export interface QueueSection extends Page<SectionRow> {
  key: SectionKey;
  title: string;
  /** The order the server used: a QueueSort, or an employee sort on "all". */
  sort: string;
}

export interface StaffQueueResponse {
  sections: QueueSection[];
  meta: StaffQueueMeta;
}

export type TransitionAction =
  | "confirm_clearance"
  | "not_found"
  | "advance"
  | "entered"
  | "badge_made"
  | "delivered"
  | "photo_on_file"
  | "set_stage";

export interface TransitionBody {
  vendorId: string;
  etag: string;
  action: TransitionAction;
  /** YYYY-MM-DD, the date the Clearinghouse shows. */
  clearanceDate?: string;
  photoOnFile?: boolean;
  /** set_stage only. */
  toStatus?: EmployeeStatus;
  /** set_stage only. */
  reason?: string;
}

export interface PaymentRow {
  date: string;
  /** Null when the vendor document cannot be found; the row still shows. */
  vendorCompanyName: string | null;
  vendorHidden: boolean;
  orderType: OrderType;
  orderTypeLabel: string;
  amount: number;
  vancoPaymentID: string;
  cardLast4: string;
  status: Order["status"];
  orderId: string;
  /** Null on pre-v2 and referencing orders. */
  lines: OrderLine[] | null;
  reconcileNeeded: boolean;
}

// --- Token management ---

/** The staff refresh token is kept for this tab, so a reload or a same-tab deep
 *  link keeps the session. Staff pages are not embedded in the ptdiocese.org
 *  iframe, but storage can still be blocked: every access is wrapped, and a
 *  blocked store means today's memory-only behaviour. The access token is never
 *  stored. Decision: decisions/2026-10-03-staff-refresh-token-in-session-storage.md */
export const STAFF_REFRESH_STORAGE_KEY = "vpd.staff.refreshToken";

let _accessToken: string | null = null;
let _refreshToken: string | null = null;
let _onAuthFailure: (() => void) | null = null;
let _refreshPromise: Promise<StaffUser | null> | null = null;
let _restorePromise: Promise<StaffUser | null> | null = null;

function storeRefreshToken(token: string | null): void {
  try {
    if (token === null) sessionStorage.removeItem(STAFF_REFRESH_STORAGE_KEY);
    else sessionStorage.setItem(STAFF_REFRESH_STORAGE_KEY, token);
  } catch {
    // Blocked storage: the session lives in memory only, as before.
  }
}

function readStoredRefreshToken(): string | null {
  try {
    return sessionStorage.getItem(STAFF_REFRESH_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function hasStoredStaffSession(): boolean {
  return readStoredRefreshToken() !== null;
}

export function setStaffTokens(access: string, refresh: string): void {
  _accessToken = access;
  _refreshToken = refresh;
  storeRefreshToken(refresh);
}

export function clearStaffTokens(): void {
  _accessToken = null;
  _refreshToken = null;
  storeRefreshToken(null);
}

export function onStaffAuthFailure(callback: () => void): void {
  _onAuthFailure = callback;
}

/** On page load: exchange the tab's stored refresh token for a fresh session.
 *  Deduplicated while in flight — React StrictMode mounts effects twice in
 *  development, and two refreshes with one token would trip the server's replay
 *  gate, whose 401 would clear the session the first one restored.
 *  - nothing stored: null, no request
 *  - success: the staff profile; the rotated token is stored
 *  - 401: null; clearStaffTokens has removed the stored token
 *  - anything else: null; the stored token is KEPT for the next load, and
 *    nothing is held in memory */
export function restoreStaffSession(): Promise<StaffUser | null> {
  if (_restorePromise) return _restorePromise;
  const stored = readStoredRefreshToken();
  if (!stored) return Promise.resolve(null);
  _refreshToken = stored;
  _restorePromise = runRefresh()
    .catch((restoreErr: unknown) => {
      if (!(restoreErr instanceof AuthError)) {
        _accessToken = null;
        _refreshToken = null;
      }
      return null;
    })
    .finally(() => {
      _restorePromise = null;
    });
  return _restorePromise;
}

// --- Core fetch (the same semantics as api.ts's apiFetch) ---

async function refreshStaffAccessToken(): Promise<StaffUser | null> {
  if (!_refreshToken) {
    throw new AuthError("No refresh token available");
  }

  const response = await fetch("/api/staff/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: _refreshToken }),
  });

  let body: ApiResponse<{ staff?: StaffUser; accessToken: string; refreshToken: string }>;
  try {
    body = (await response.json()) as typeof body;
  } catch {
    // A gateway error page, not an answer about the token. Keep the tokens.
    throw new ApiError(`Request failed (${response.status})`, "NON_JSON_RESPONSE", response.status);
  }

  if (!response.ok || body.error) {
    const message = body.error?.message ?? "Token refresh failed";
    const code = body.error?.code ?? "REFRESH_FAILED";
    // Only a 401 says the refresh token itself was rejected. A 503, a 429 or
    // a 5xx is the service failing and must not sign staff out.
    if (response.status === 401) {
      clearStaffTokens();
      throw new AuthError(message, code, 401);
    }
    throw new ApiError(message, code, response.status);
  }

  _accessToken = body.data!.accessToken;
  _refreshToken = body.data!.refreshToken;
  storeRefreshToken(_refreshToken);
  return body.data!.staff ?? null;
}

function runRefresh(): Promise<StaffUser | null> {
  if (!_refreshPromise) {
    _refreshPromise = refreshStaffAccessToken().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
}

export async function staffApiFetch<T>(
  url: string,
  options: RequestInit = {},
): Promise<{ data: T; meta: Record<string, unknown> }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };

  // A 401 can only end a session that exists: a failed login is a 401 too.
  const hadSession = _accessToken !== null || _refreshToken !== null;

  if (_accessToken) {
    // X-VPD-Auth because Azure SWA overwrites the standard Authorization header.
    headers["X-VPD-Auth"] = `Bearer ${_accessToken}`;
  }

  let response = await fetch(url, { ...options, headers });

  if (response.status === 401 && _refreshToken) {
    try {
      // Deduplicate concurrent refresh attempts.
      await runRefresh();

      headers["X-VPD-Auth"] = `Bearer ${_accessToken}`;
      response = await fetch(url, { ...options, headers });
    } catch (refreshErr) {
      // End the session only when the refresh token was actually rejected.
      if (refreshErr instanceof AuthError) {
        _onAuthFailure?.();
        throw new AuthError("Session expired. Please sign in again.");
      }
      throw refreshErr;
    }
  }

  let body: ApiResponse<T>;
  try {
    body = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(`Request failed (${response.status})`, "NON_JSON_RESPONSE", response.status);
  }

  if (!response.ok || body.error) {
    const errorMessage = body.error?.message ?? "An unexpected error occurred";
    const errorCode = body.error?.code ?? "API_ERROR";

    if (response.status === 401) {
      if (hadSession) {
        _onAuthFailure?.();
      }
      throw new AuthError(errorMessage, errorCode, 401);
    }
    throw new ApiError(errorMessage, errorCode, response.status);
  }

  return { data: body.data as T, meta: body.meta };
}

// --- Typed endpoint functions ---

export async function staffLogin(
  email: string,
  password: string,
): Promise<{ staff: StaffUser; accessToken: string; refreshToken: string }> {
  const { data } = await staffApiFetch<{ staff: StaffUser; accessToken: string; refreshToken: string }>(
    "/api/staff/auth/login",
    { method: "POST", body: JSON.stringify({ email, password }) },
  );
  return data;
}

/** The server answers 200 whether or not the address has an account. */
export async function staffForgotPassword(email: string): Promise<void> {
  await staffApiFetch<{ message: string }>("/api/staff/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/** Sets the password from an invite or reset link's token. */
export async function staffSetPassword(token: string, password: string): Promise<void> {
  await staffApiFetch<{ message: string }>("/api/staff/auth/set-password", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

/** One dashboard view. `params` are the page's list parameters (page_<key> and
 *  sort_<key> on a queue; page, sort, dir, stage, includeInactive and
 *  includeHidden on "all"), passed through unchanged. */
export async function getStaffEmployees(
  view: StaffView,
  params: URLSearchParams = new URLSearchParams(),
): Promise<StaffQueueResponse> {
  const query = new URLSearchParams({ view });
  params.forEach((value, key) => {
    if (key !== "view") query.append(key, value);
  });
  const { data, meta } = await staffApiFetch<{ sections: QueueSection[] }>(`/api/staff/employees?${query.toString()}`);
  return { sections: data.sections, meta: meta as unknown as StaffQueueMeta };
}

/** A company as the staff directory sees it. */
export interface CompanySummary {
  id: string;
  companyName: string;
  contactName: string;
  contactPhone: string;
  email: string;
  createdAt: string;
  hidden: boolean;
}

/** A Companies-tab row. */
export interface CompanyRow extends CompanySummary {
  employeeCount: number;
  waitingOnYou: number;
  lastOrderAt: string | null;
}

const withQuery = (path: string, params: URLSearchParams): string => {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

export async function getStaffCompanies(params: URLSearchParams = new URLSearchParams()): Promise<Page<CompanyRow>> {
  const { data } = await staffApiFetch<Page<CompanyRow>>(withQuery("/api/staff/companies", params));
  return data;
}

/** A company search result, with what the query matched. */
export type SearchCompanyRow = CompanyRow & { matched: "company" | "contact" | "email" };

export interface SearchResponse {
  companies: Page<SearchCompanyRow>;
  employees: Page<StaffEmployeeRow>;
}

export async function searchStaff(params: URLSearchParams): Promise<SearchResponse> {
  const { data } = await staffApiFetch<SearchResponse>(withQuery("/api/staff/search", params));
  return data;
}

/** One order on a company page: the facts Accounting's Payments page shows. */
export interface CompanyOrderRow {
  id: string;
  createdAt: string;
  type: OrderType;
  typeLabel: string;
  lines: OrderLine[] | null;
  totalAmount: number;
  quantity: number;
  vancoTransactionId: string;
  paymentMethodLast4: string;
  status: Order["status"];
  reconcileNeeded: boolean;
}

export interface CompanyResponse {
  company: CompanySummary;
  employees: Page<StaffEmployeeRow>;
  orders: Page<CompanyOrderRow>;
}

export async function getStaffCompany(id: string, params: URLSearchParams = new URLSearchParams()): Promise<CompanyResponse> {
  const { data } = await staffApiFetch<CompanyResponse>(withQuery(`/api/staff/companies/${encodeURIComponent(id)}`, params));
  return data;
}

/** IT only. */
export async function setCompanyVisibility(id: string, hidden: boolean): Promise<{ id: string; hidden: boolean }> {
  const { data } = await staffApiFetch<{ id: string; hidden: boolean }>(
    `/api/staff/companies/${encodeURIComponent(id)}/visibility`,
    { method: "POST", body: JSON.stringify({ hidden }) },
  );
  return data;
}

export async function postTransition(
  id: string,
  body: TransitionBody,
): Promise<{ employee: StaffEmployee; etag: string }> {
  const { data } = await staffApiFetch<{ employee: StaffEmployee; etag: string }>(
    `/api/staff/employees/${encodeURIComponent(id)}/transition`,
    { method: "POST", body: JSON.stringify(body) },
  );
  return data;
}

export async function fulfillReplacement(orderId: string, vendorId: string, employeeId: string): Promise<void> {
  await staffApiFetch<{ order: Order }>(
    `/api/staff/replacement-orders/${encodeURIComponent(orderId)}/fulfill`,
    { method: "POST", body: JSON.stringify({ vendorId, employeeId }) },
  );
}

/** start and end are ISO-8601 instants, end exclusive. The server defaults a
 *  missing window to the last 30 days. */
export async function getStaffPayments(start?: string, end?: string): Promise<{ payments: PaymentRow[] }> {
  const params = new URLSearchParams();
  if (start !== undefined) params.set("start", start);
  if (end !== undefined) params.set("end", end);
  const query = params.toString();
  const { data } = await staffApiFetch<{ payments: PaymentRow[] }>(
    `/api/staff/payments${query ? `?${query}` : ""}`,
  );
  return data;
}

/** One line of an employee's history (server: lib/employee-timeline.ts). `who`
 *  is a name, "Vendor", "Former staff member", or null. */
export type TimelineEntry =
  | { kind: "stage"; at: string; who: string | null; action: StageHistoryAction; from: EmployeeStatus; to: EmployeeStatus }
  | {
      kind: "correction";
      at: string;
      who: string | null;
      from: EmployeeStatus;
      to: EmployeeStatus;
      reason: string;
      previousClearanceDate: string | null;
    }
  | { kind: "clearance"; at: string; who: string | null; clearanceDate: string }
  | { kind: "added"; at: string };

export interface EmployeeDetailResponse {
  employee: StaffEmployeeRow;
  orders: CompanyOrderRow[];
  timeline: TimelineEntry[];
  /** YYYY-MM-DD: nothing before it was recorded. */
  historyStartsOn: string;
}

export async function getStaffEmployee(
  vendorId: string,
  id: string,
  params: URLSearchParams = new URLSearchParams(),
): Promise<EmployeeDetailResponse> {
  const { data } = await staffApiFetch<EmployeeDetailResponse>(
    withQuery(`/api/staff/employees/${encodeURIComponent(vendorId)}/${encodeURIComponent(id)}`, params),
  );
  return data;
}

/** A list's parameters as a download asks for them: every row, no page. */
function exportParams(params: URLSearchParams): URLSearchParams {
  const out = new URLSearchParams(params);
  out.delete("page");
  out.set("export", "true");
  return out;
}

/** Every row of the Employees table as filtered and sorted, for the CSV. */
export async function exportStaffEmployees(params: URLSearchParams): Promise<StaffEmployeeRow[]> {
  const { sections } = await getStaffEmployees("all", exportParams(params));
  return (sections[0]?.rows ?? []).flatMap((r) => (r.kind === "employee" ? [r.row] : []));
}

/** Every row of the Companies table as sorted, for the CSV. */
export async function exportStaffCompanies(params: URLSearchParams): Promise<CompanyRow[]> {
  return (await getStaffCompanies(exportParams(params))).rows;
}
